import { Icon, IconProps, usePopover } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Button, Stack, Typography } from '@mui/material';
import { mergeSx } from 'merge-sx';
import { memo, useEffect, useState } from 'react';
import { useRecordManagementLabels } from '../record-management/hooks/use-record-management-labels';
import { LabelManagementPopover } from './components/label-management-popover';
import { RecordManagementLabel } from './components/record-management-label';
import * as Styled from './styles';
import * as SX from './sx';
import type { RecordManagementLabelsProps as Props } from './types';
import { IconWithCounter } from '@aibees/elephant/feature/sidebar-lookalike/components/icon-with-counter';

export const RecordManagementLabels = memo(
  ({
    className,
    onCountChange,
    labelType,
    iconAddButton,
    isViewAll: initialIsViewAll,
    compact,
    sx,
  }: Props) => {
    const popoverState = usePopover();
    const {
      id,
      filteredLabels,
      emptyTitle,
      title,
      recordLabel,
      icon,
      lastItem,
      labelsCount,
    } = useRecordManagementLabels(labelType);
    const [isViewAll, setIsViewAll] = useState(initialIsViewAll ?? false);

    useEffect(() => {
      setIsViewAll(initialIsViewAll ?? false);
    }, [initialIsViewAll]);

    useEffect(() => {
      onCountChange?.(labelsCount);
    }, [labelsCount, onCountChange]);

    const handleOpenPopover: IconProps['onClick'] = (e) => {
      popoverState.onOpen(e.currentTarget);
    };

    const handleToggleView = () => {
      setIsViewAll((prev) => !prev);
    };

    const renderFilteredLabels = () => {
      if (compact) return null;
      return filteredLabels.length ? (
        <Stack sx={SX.labelWrapper}>
          {isViewAll ? (
            filteredLabels.map((i) => (
              <RecordManagementLabel key={id} label={i} id={id as string} />
            ))
          ) : (
            <RecordManagementLabel
              key={lastItem?.name}
              label={lastItem!}
              id={id as string}
            />
          )}
        </Stack>
      ) : (
        <Typography variant="body4" fontWeight={500} color="grey.400">
          {emptyTitle}
        </Typography>
      );
    };
    return (
      <Styled.Wrapper
        className={classnames('RecordManagementLabels', className)}
        sx={mergeSx(SX.root, sx)}
      >
        <Stack sx={SX.header}>
          {renderFilteredLabels()}
          <IconWithCounter
            iconName={iconAddButton}
            count={filteredLabels.length}
            onClick={handleOpenPopover}
          />
        </Stack>

        {filteredLabels.length > 1 && isViewAll && (
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={handleToggleView}
          >
            {isViewAll ? 'View Less' : `+${filteredLabels.length - 1} View All`}
          </Button>
        )}

        <LabelManagementPopover
          state={popoverState}
          id={id}
          callSaveApiOnSubmit
          parent="Record Management"
          currentLists={filteredLabels}
          title={
            <Typography variant="body4" sx={SX.popoverTitle}>
              {title}
            </Typography>
          }
          recordLabel={recordLabel}
          icon={<Icon name={icon} size={20} color="grey.500" />}
          labelType={labelType}
        />
      </Styled.Wrapper>
    );
  }
);

RecordManagementLabels.displayName = 'RecordManagementLabels';
